// biome-ignore-all lint/a11y/noNoninteractiveTabindex: Informational tooltip triggers must be keyboard focusable.
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import { MOCK_LIBRARY_ITEMS } from '../../_mock-data/library'
import { TypeDot } from '../../shared/pills'
import { useCoachingStore } from '../../store'
import { bandFields, eloContext } from '../_lib/elo-band'
import {
  evalCompanyTags,
  evalFilters,
  type LibraryItem,
  topicPairLabel,
  topicPairsOf,
} from '../_lib/filter-engine'
import { formatLastSeen } from '../_lib/last-seen'
import { matchLibraryItems } from '../_lib/library-search'
import { itemIsSeen, type SeenFilter } from '../_lib/seen-filter'
import { applySorts, type SortContext, setPrimarySort } from '../_lib/sorting'
import type { SourceOption } from '../_lib/source-option'
import { CorpusStatus } from '../shared/corpus-status'
import { AssignmentActions } from './assignment-actions'
import { ColumnMenu, type ColumnOption } from './column-menu'
import { SourceCell } from './source-cell'

export interface BrowseTableProps {
  items?: LibraryItem[]
  query?: string
  variant?: 'browse' | 'topic'
  emptyState?: ReactNode
}

interface SelectionState {
  filters: ReturnType<typeof useCoachingStore>['curriculum']['filters']
  sorts: ReturnType<typeof useCoachingStore>['curriculum']['sorts']
  typeFilter: ReturnType<typeof useCoachingStore>['curriculum']['typeFilter']
  companyTags: ReturnType<typeof useCoachingStore>['curriculum']['companyTags']
  seenFilter: SeenFilter
}

export function selectLibraryItems(
  items: LibraryItem[],
  state: SelectionState,
  query: string,
  // Fuzzy, over the full corpus (LG-362 §6) — matched once up front, then
  // intersected with the other filters per row. See `matchLibraryItems` for
  // which fields are searched and why. The component precomputes this in a
  // memo keyed on [items, query] so index builds don't recur on unrelated
  // store changes; the default keeps the selector self-contained for tests
  // and any caller without render-loop concerns.
  matched: ReadonlySet<string> | null = matchLibraryItems(items, query),
  // Sorting alone follows the session's per-row source picks (LG-362 §3):
  // the table sorts what it shows. Filtering above stays on the stored data
  // so a pick never removes a row from a source-filtered view mid-click.
  sortContext: SortContext = {},
): LibraryItem[] {
  const filtered = items.filter((item) => {
    if (state.typeFilter !== 'all' && item.type !== state.typeFilter)
      return false
    if (
      state.seenFilter !== 'all' &&
      itemIsSeen(item) !== (state.seenFilter === 'seen')
    )
      return false
    if (!evalFilters(state.filters, item)) return false
    if (!evalCompanyTags(state.companyTags, item)) return false
    return matched === null || matched.has(item.id)
  })
  return applySorts(state.sorts, filtered, sortContext)
}

interface TableColumn extends ColumnOption {
  width: string
  // Numeric columns align right, header and cells together, so the header
  // never reads as offset from its data. Text columns stay left.
  align?: 'right'
}

// No Source column: the source mark is pinned on the title with the external
// id (LG-362 §3 revision), and it is deliberately absent from the column menu
// too — hiding it would strand the only way to switch a row's source.
const BROWSE_COLUMNS = [
  // Default position just right of the title (LG-362 §8); movable and
  // hideable like any real column, but only *present* while the seen pill
  // includes seen rows — see `supportedColumns`.
  { key: 'lastSeen', label: 'Last seen', width: 'minmax(84px,.8fr)' },
  { key: 'topic', label: 'Topic / Sub', width: 'minmax(100px,1fr)' },
  { key: 'difficulty', label: 'Diff', width: '44px' },
  { key: 'elo', label: 'ELO', width: '54px', align: 'right' },
  { key: 'plans', label: 'Plans', width: 'minmax(78px,.8fr)' },
  { key: 'company', label: 'Company', width: 'minmax(78px,.8fr)' },
] as const satisfies readonly TableColumn[]

// The external id is attached to the title, LeetCode-style (LG-362 §3) — it
// owns no grid track and never reorders, but the column menu still offers it
// for hiding, pinned ahead of the real columns.
const EXT_ID_MENU_COLUMN = {
  key: 'extId',
  label: 'Ext ID',
  movable: false,
} as const satisfies ColumnOption

const TOPIC_COLUMNS = [
  { key: 'fit', label: '✦ Fit', width: '64px' },
  { key: 'elo', label: 'ELO', width: '54px', align: 'right' },
  { key: 'difficulty', label: 'Diff', width: '44px' },
  { key: 'hours', label: '~H', width: '38px', align: 'right' },
  { key: 'plans', label: 'Plans', width: 'minmax(78px,.8fr)' },
] as const satisfies readonly TableColumn[]

// Literal union of every real column key, so the companion maps below are
// compile-checked: adding a column without deciding its sortability or
// per-mode support is a type error, not a silently broken header.
type ColumnKey =
  | (typeof BROWSE_COLUMNS)[number]['key']
  | (typeof TOPIC_COLUMNS)[number]['key']

const alignClass = (column: TableColumn): string | undefined =>
  column.align === 'right' ? 'text-right' : undefined

// Columns named in `order` come first, in that order; columns the stored
// order does not know about (e.g. added after the preference was saved)
// keep their default relative position at the end.
export function applyColumnOrder<T extends ColumnOption>(
  columns: readonly T[],
  order: string[],
): readonly T[] {
  if (order.length === 0) return columns
  const rank = new Map(order.map((key, index) => [key, index]))
  return [...columns].sort((left, right) => {
    const leftRank = rank.get(left.key)
    const rightRank = rank.get(right.key)
    if (leftRank === undefined && rightRank === undefined) return 0
    if (leftRank === undefined) return 1
    if (rightRank === undefined) return -1
    return leftRank - rightRank
  })
}

const SORT_FIELDS: Record<
  ColumnKey | 'title',
  Parameters<typeof setPrimarySort>[1] | null
> = {
  topic: 'topic',
  difficulty: 'difficulty',
  elo: 'elo',
  plans: 'plans',
  company: 'company',
  title: 'title',
  lastSeen: 'lastSeen',
  fit: null,
  hours: null,
}

const DIFFICULTY_COLOR: Record<LibraryItem['difficulty'], string> = {
  Easy: 'var(--cd-green)',
  Med: 'var(--cd-amber)',
  Hard: 'var(--cd-red)',
  '—': 'var(--cd-text-faint)',
}

const TYPE_NAME: Record<LibraryItem['type'], string> = {
  coding: 'Coding',
  sd: 'System design',
  behavioral: 'Behavioral',
  interview: 'Interview',
  other: 'Other',
}

const signed = (value: number): string =>
  value > 0 ? `+${value}` : String(value)

/**
 * The title-attached external id (LG-362 §3), inert text: the id shown is the
 * worn source's, so the expanded source mark beside it already names every
 * other source and its id — the old duplicates tooltip said the same thing
 * twice.
 */
function TitleExternalId(props: { extId: string | null }): ReactNode {
  if (props.extId === null) return null
  return (
    <span className="mr-1 shrink-0 font-mono text-[10px] font-normal text-(--cd-text-faint)">
      {props.extId}.
    </span>
  )
}

/**
 * A glyph after the Last-seen date qualifying whose date it is — the coach's
 * report, or a duplicate twin's sighting. The label is both the tooltip and
 * the accessible name, so the two never drift apart.
 */
function LastSeenMarker(props: { glyph: string; label: string }): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          tabIndex={0}
          aria-label={props.label}
          className="shrink-0 text-(--cd-text-faint)"
        >
          {props.glyph}
        </span>
      </TooltipTrigger>
      <TooltipContent>{props.label}</TooltipContent>
    </Tooltip>
  )
}

const PERSISTED_UNSUPPORTED_COLUMNS = new Set<ColumnKey>(['plans', 'company'])

const NO_SORT_CONTEXT: SortContext = {}

export function BrowseTable(props: BrowseTableProps): ReactNode {
  const {
    items = MOCK_LIBRARY_ITEMS,
    query = '',
    variant = 'browse',
    emptyState,
  } = props
  const { curriculum } = useCoachingStore()
  const [menuPosition, setMenuPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [openRowTooltipId, setOpenRowTooltipId] = useState<string | null>(null)
  // Session-only source picks, item id → sourceId (LG-362 §3 revision). A
  // pick swaps which identity the row wears — mark, external id, and what
  // +Add / Tray stage — and deliberately does not persist: the owning source
  // stays the stored truth, the pick is a viewing/adding convenience.
  const [pickedSources, setPickedSources] = useState<Record<string, string>>({})
  const suppressedRowTooltipId = useRef<string | null>(null)
  const menuInvokerRef = useRef<HTMLElement | null>(null)
  const titleHeaderButtonRef = useRef<HTMLButtonElement>(null)
  const restoreMenuFocusRef = useRef(false)

  // `items` is the whole drained corpus now (LG-362), not a 100-row page, and
  // this component re-renders on ordinary hover (`setOpenRowTooltipId`). Left
  // unmemoized, moving the mouse across the table re-filters and re-sorts
  // every row once per row entered. `curriculum` is memoized upstream
  // (store.tsx), so it is stable across those local-state renders — depending
  // on the spread `selectionState` instead would defeat the memo entirely,
  // which is why it is computed inside.
  // Fuzzy matching is the expensive leg (mapping the corpus and building a
  // Fuse index), and it depends only on the corpus and the query — so it gets
  // its own memo. Folded into `matching` below it would rebuild the index on
  // every `curriculum` change while a query is active (sort toggles, filter
  // edits, company-tag cycles).
  const matched = useMemo(() => matchLibraryItems(items, query), [items, query])
  // The session pick, validated against the row's current options: a pick
  // whose source vanished from a refetched corpus resolves to null, so
  // display, sorting, AND staging all agree on the fallback identity — the
  // raw map entry must never leave this resolver.
  const pickedOption = useCallback(
    (item: LibraryItem): SourceOption | null => {
      const picked = pickedSources[item.id]
      if (picked === undefined) return null
      return (
        (item.sources ?? []).find((option) => option.sourceId === picked) ??
        null
      )
    },
    [pickedSources],
  )
  // The identity a row currently wears: the validated pick when one exists,
  // else the owning source by flag (never list position — the repo guardrail
  // on primary-row selection), else the first option for malformed data.
  const wornSource = useCallback(
    (item: LibraryItem): SourceOption | null => {
      const sources = item.sources ?? []
      return (
        pickedOption(item) ??
        sources.find((option) => option.owning) ??
        sources[0] ??
        null
      )
    },
    [pickedOption],
  )
  // The id shown on the title, which the ext-id sort must follow (the table
  // sorts what it shows). A deliberate pick shows exactly that source's id —
  // absent means absent. At rest, an owning source without an id falls back
  // to the stored `extId` rather than hiding a number the row showed before
  // the sources hydration existed.
  const wornExtId = useCallback(
    (item: LibraryItem): string | null => {
      if ((item.sources?.length ?? 0) === 0) return null
      const picked = pickedOption(item)
      if (picked !== null) return picked.externalId
      return wornSource(item)?.externalId ?? item.extId
    },
    [pickedOption, wornSource],
  )
  // Sorting consults the worn id only while an ext-id sort is active; the
  // stable empty context otherwise keeps every pick from re-filtering and
  // re-sorting the whole corpus for nothing (the §6 memo discipline).
  const extIdSort = useMemo<SortContext>(
    () =>
      curriculum.sorts.some((sort) => sort.field === 'extId')
        ? { extIdOf: wornExtId }
        : NO_SORT_CONTEXT,
    [curriculum.sorts, wornExtId],
  )
  const matching = useMemo(() => {
    // Persisted mode evaluates both the filter tree AND the type pill (kinds)
    // server-side, so the client engine must not re-filter rows: it cannot see
    // plan/difficulty data, and re-applying the type pill to the previous page
    // (placeholderData) would blank the table while a pill change refetches.
    // Feeding an empty tree and 'all' lets the shared selector run unchanged
    // while the server stays the sole evaluator.
    const base = curriculum.persisted
      ? { ...curriculum, filters: [], typeFilter: 'all' as const }
      : curriculum
    // The seen filter alone stays client-side in both modes, but is suspended
    // while the per-client overlay is pending — filtering before it arrives
    // would call every row unseen and reshuffle when the map lands.
    const selectionState = {
      ...base,
      seenFilter: curriculum.seenOverlayPending
        ? ('all' as const)
        : curriculum.seenFilter,
    }
    return selectLibraryItems(items, selectionState, query, matched, extIdSort)
  }, [items, curriculum, query, matched, extIdSort])
  // Persisted browse evaluates filters server-side; while that refetch is in
  // flight the previous page stays on screen (placeholderData). Dim + mark it
  // busy so the briefly-stale rows read as updating, not authoritative.
  const refreshing =
    variant === 'browse' && curriculum.browseRefreshing === true
  // Corpus state belongs to the library only; the topic table draws from a
  // different, already-complete set. Which of the two wins when both are set
  // is `CorpusStatus`'s call, off the hook's `isTruncated`.
  const streaming = variant === 'browse' && curriculum.browseStreaming === true
  const incomplete =
    variant === 'browse' && curriculum.browseIncomplete === true
  const supportedColumns = (
    variant === 'browse' ? BROWSE_COLUMNS : TOPIC_COLUMNS
  ).filter(
    (column) =>
      (!curriculum.persisted ||
        !PERSISTED_UNSUPPORTED_COLUMNS.has(column.key)) &&
      // "Last seen" exists only while the pill includes seen rows (LG-362
      // §8) — gone from the grid AND the column menu in unseen-only. An
      // explicit hide is a separate, respected preference (hiddenCols).
      (column.key !== 'lastSeen' || curriculum.seenFilter !== 'unseen'),
  )
  const sourceColumns =
    variant === 'browse'
      ? applyColumnOrder(supportedColumns, curriculum.columnOrder)
      : supportedColumns
  const visibleColumns = sourceColumns.filter(
    (column) => !curriculum.hiddenCols.includes(column.key),
  )
  // The topic table never carried an id column, so the title attachment is a
  // browse-only affordance — its menu entry too.
  const showExtId =
    variant === 'browse' &&
    !curriculum.hiddenCols.includes(EXT_ID_MENU_COLUMN.key)
  const menuColumns =
    variant === 'browse'
      ? [EXT_ID_MENU_COLUMN, ...sourceColumns]
      : sourceColumns
  const gridTemplate = [
    '14px',
    'minmax(150px,1.5fr)',
    ...visibleColumns.map((column) => column.width),
    '94px',
  ].join(' ')
  const primarySort = curriculum.sorts[0]
  // One clock read per render, shared by every "Last seen" cell so two rows
  // in the same paint can never disagree about what "today" means.
  const now = new Date()
  // Shared with the header legend so the cells, their tooltips, and the key
  // can never quote three different bands for one column of colours.
  const elo = eloContext(curriculum)

  const openColumnMenu = (event: React.MouseEvent): void => {
    if (!(event.target instanceof Element)) return
    const header = event.target.closest<HTMLElement>('[role="columnheader"]')
    if (!header || !event.currentTarget.contains(header)) return

    event.preventDefault()
    const nestedButton = event.target.closest<HTMLElement>('button')
    menuInvokerRef.current =
      nestedButton && header.contains(nestedButton) ? nestedButton : header
    restoreMenuFocusRef.current = false
    setMenuPosition({ x: event.clientX, y: event.clientY + 4 })
  }

  const openColumnMenuFromButton = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    menuInvokerRef.current = event.currentTarget
    restoreMenuFocusRef.current = false
    const rect = event.currentTarget.getBoundingClientRect()
    setMenuPosition({ x: rect.left, y: rect.bottom + 4 })
  }

  const reorderColumn = (key: string, toIndex: number): void => {
    const keys: string[] = sourceColumns
      .map((column) => column.key)
      .filter((candidate) => candidate !== key)
    keys.splice(Math.max(0, Math.min(toIndex, keys.length)), 0, key)
    // The stored order must rank EVERY real column, not just the ones the
    // current mode/pill lets the menu show — one drag made while "Last
    // seen" is gated out (unseen-only) would otherwise save an order with
    // no entry for it and exile it to the far end for good. Each absent
    // column slots back in after the column it currently follows.
    const fullOrder = applyColumnOrder(BROWSE_COLUMNS, curriculum.columnOrder)
    const placed = new Set(keys)
    let anchor: string | null = null
    for (const column of fullOrder) {
      if (placed.has(column.key)) {
        anchor = column.key
        continue
      }
      keys.splice(anchor === null ? 0 : keys.indexOf(anchor) + 1, 0, column.key)
      placed.add(column.key)
      anchor = column.key
    }
    curriculum.setColumnOrder(keys)
  }

  const closeColumnMenu = (): void => {
    restoreMenuFocusRef.current = true
    setMenuPosition(null)
  }

  useEffect(() => {
    if (menuPosition !== null || !restoreMenuFocusRef.current) return

    const invoker = menuInvokerRef.current
    const focusTarget =
      invoker?.isConnected === true ? invoker : titleHeaderButtonRef.current
    focusTarget?.focus()
    restoreMenuFocusRef.current = false
  }, [menuPosition])

  const sortHeader = (key: ColumnKey | 'title'): void => {
    const field = SORT_FIELDS[key]
    if (!field) return
    curriculum.setSorts(setPrimarySort(curriculum.sorts, field))
  }

  const headerLabel = (column: { key: ColumnKey; label: string }): string => {
    const field = SORT_FIELDS[column.key]
    if (!field || primarySort?.field !== field) return column.label
    return `${column.label} ${primarySort.dir === 'asc' ? '▲' : '▼'}`
  }

  // One definition, rendered in exactly one of two mutually exclusive spots:
  // the table's actions header normally, and the count row when the empty
  // state has replaced the table — otherwise the column menu would be
  // unreachable on a tab whose filters currently match nothing.
  const columnsControl = (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuPosition !== null}
          aria-label="Columns"
          onClick={openColumnMenuFromButton}
          // size-6 keeps the sole entry point to the column menu at the 24×24
          // minimum target size now that it is an icon rather than a pill.
          className="flex size-6 items-center justify-center rounded-full border border-(--cd-border-2) text-[11px] leading-none text-(--cd-text-muted) hover:text-(--cd-text)"
        >
          ⊞
        </button>
      </TooltipTrigger>
      <TooltipContent>Show columns</TooltipContent>
    </Tooltip>
  )
  const showEmptyState = matching.length === 0 && emptyState !== undefined

  return (
    // `xl:` classes throughout carry the row splitter's height (LG-611) down
    // to the rowgroup: the match-count line stays put and the rows scroll in
    // whatever is left. Below xl: the fixed max-h below is still the cap.
    <div className="space-y-2 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      {/* The ELO shade key moved up into the Library card header (LG-362 §7);
          this row now carries the match count and, when the corpus behind it
          is still arriving or arrived short, says so. */}
      <div className="flex items-center gap-2 xl:shrink-0">
        {/* The count keeps this element to itself: several suites assert its
            exact text content, so any hint must be a SIBLING, never a child. */}
        <p className="text-[10px] text-(--cd-text-faint)">
          {matching.length} of {items.length} matching
        </p>
        {variant === 'browse' ? (
          <CorpusStatus
            label="Library loading status"
            incomplete={incomplete}
            streaming={streaming}
          />
        ) : null}
        {showEmptyState ? (
          <span className="ml-auto">{columnsControl}</span>
        ) : null}
      </div>

      {showEmptyState ? (
        emptyState
      ) : (
        <div className="overflow-x-auto rounded-lg border border-(--cd-border) xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
          <div
            role="table"
            aria-busy={refreshing || undefined}
            aria-label={
              variant === 'browse'
                ? 'Library browse results'
                : 'Topic recommendations'
            }
            className={`min-w-[680px] bg-(--cd-card) transition-opacity xl:flex xl:min-h-0 xl:flex-1 xl:flex-col ${refreshing ? 'opacity-60' : ''}`}
          >
            <div
              role="row"
              onContextMenu={openColumnMenu}
              className="grid items-center gap-2 border-b border-(--cd-border) bg-(--cd-inset) px-2 py-1.5 xl:shrink-0"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              <span role="columnheader" aria-label="Type" tabIndex={-1} />
              <span
                role="columnheader"
                tabIndex={-1}
                aria-sort={
                  primarySort?.field === 'title'
                    ? primarySort.dir === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                <button
                  ref={titleHeaderButtonRef}
                  type="button"
                  onClick={() => sortHeader('title')}
                  className="text-left text-[9px] font-bold uppercase tracking-[0.06em] text-(--cd-text-faint)"
                >
                  {primarySort?.field === 'title'
                    ? `Title ${primarySort.dir === 'asc' ? '▲' : '▼'}`
                    : 'Title'}
                </button>
              </span>
              {visibleColumns.map((column) => (
                <span
                  role="columnheader"
                  tabIndex={-1}
                  key={column.key}
                  className={alignClass(column)}
                  aria-sort={
                    SORT_FIELDS[column.key]
                      ? primarySort?.field === SORT_FIELDS[column.key]
                        ? primarySort.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                      : undefined
                  }
                >
                  {SORT_FIELDS[column.key] ? (
                    <button
                      type="button"
                      onClick={() => sortHeader(column.key)}
                      className="text-[9px] font-bold uppercase tracking-[0.06em] text-(--cd-text-faint)"
                    >
                      {headerLabel(column)}
                    </button>
                  ) : (
                    <span className="text-[9px] font-bold uppercase tracking-[0.06em] text-(--cd-text-faint)">
                      {column.label}
                    </span>
                  )}
                </span>
              ))}
              {/* The actions column is unlabelled dead space in the header, so
                  it hosts the icon-only Columns control directly above the
                  per-row + Add / Tray buttons (LG-362 §7). */}
              <span
                role="columnheader"
                aria-label="Actions"
                tabIndex={-1}
                className="flex justify-end"
              >
                {columnsControl}
              </span>
            </div>

            <div
              role="rowgroup"
              aria-label={
                variant === 'browse'
                  ? 'Library browse result rows'
                  : 'Topic recommendation rows'
              }
              className="max-h-[330px] overflow-y-auto xl:max-h-none xl:min-h-0 xl:flex-1"
            >
              {matching.map((item) => {
                const band =
                  item.elo === null || elo === null
                    ? null
                    : bandFields(item.elo, elo.rating, elo.band)
                const rowTip =
                  variant === 'topic' && item.rationale
                    ? item.rationale
                    : `${item.stage} stage · ${item.estimatedMinutes === null ? '—' : `~${item.est}h`}${item.repeat ? ` · ↻ ${item.repeat}` : ''}`
                const row = (
                  <div
                    role="row"
                    onFocus={(event) => {
                      if (event.target === event.currentTarget) return
                      event.preventDefault()
                      setOpenRowTooltipId(null)
                    }}
                    onPointerMove={(event) => {
                      if (!(event.target instanceof Element)) return
                      const nestedTrigger = event.target.closest(
                        '[data-slot="tooltip-trigger"]',
                      )
                      if (nestedTrigger === event.currentTarget) return
                      event.preventDefault()
                      setOpenRowTooltipId(null)
                    }}
                    className="grid items-center gap-2 border-b border-(--cd-border) px-2 py-1.5 last:border-b-0 hover:bg-(--cd-inset)"
                    style={{ gridTemplateColumns: gridTemplate }}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span role="cell" tabIndex={0}>
                          <TypeDot type={item.type} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{TYPE_NAME[item.type]}</TooltipContent>
                    </Tooltip>
                    <span
                      role="cell"
                      className="flex min-w-0 items-center text-xs font-semibold text-(--cd-text)"
                    >
                      {/* The row's pinned identity, in wear order: source
                          mark, external id, title (LG-362 §3 revision).
                          Expanding the mark pushes the id and title right. */}
                      <SourceCell
                        sources={item.sources ?? []}
                        worn={wornSource(item)}
                        onSelect={(sourceId) =>
                          setPickedSources((picks) => ({
                            ...picks,
                            [item.id]: sourceId,
                          }))
                        }
                        className="mr-1 shrink-0"
                      />
                      {showExtId && (item.sources?.length ?? 0) > 0 ? (
                        <TitleExternalId extId={wornExtId(item)} />
                      ) : null}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0} className="truncate">
                            {item.title}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{rowTip}</TooltipContent>
                      </Tooltip>
                      {item.repeat ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              tabIndex={0}
                              className="ml-1 text-[10px] text-(--cd-amber)"
                            >
                              ↻
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{item.repeat}</TooltipContent>
                        </Tooltip>
                      ) : null}
                    </span>
                    {visibleColumns.map((column) => {
                      if (column.key === 'lastSeen') {
                        // Persisted rows carry a parsed Date; mock rows only
                        // have their repeat prose; a dateless coach report is
                        // seen with nothing to show but the marker.
                        const cellText =
                          item.lastSeenAt != null
                            ? formatLastSeen(item.lastSeenAt, now)
                            : (item.repeat ?? '—')
                        return (
                          <span
                            role="cell"
                            key={column.key}
                            className="flex min-w-0 items-center gap-1 text-[10px] text-(--cd-text-muted)"
                          >
                            <span className="truncate">{cellText}</span>
                            {item.seenProvenance === 'coach' ? (
                              <LastSeenMarker
                                glyph="✎"
                                label="Reported by the coach"
                              />
                            ) : null}
                            {/* Seen through a duplicate twin (LG-442): the
                                date is real but belongs to the twin, so the
                                cell says whose it is. */}
                            {item.seenVia != null ? (
                              <LastSeenMarker
                                glyph="⇄"
                                label={`Seen as ${item.seenVia.title}`}
                              />
                            ) : null}
                          </span>
                        )
                      }
                      if (column.key === 'topic') {
                        // One line per approach pair (LG-362 §1), each
                        // truncated rather than wrapped: a multi-approach
                        // problem grows the row by a line per pair, and a
                        // long single pair ellipsizes in place. An untagged
                        // problem reads as no taxonomy, not as a blank cell.
                        //
                        // Tooltipped like the sibling truncating cells
                        // (plans, company, title) because real pairs are long
                        // — most of the canonical taxonomy exceeds this
                        // track's ~40 characters — so without it a coach
                        // cannot read why a topic filter matched the row. It
                        // also gives the stack a spoken separator: two spans
                        // in one cell are otherwise read as a single run.
                        const pairs = topicPairsOf(item)
                        return (
                          <Tooltip key={column.key}>
                            <TooltipTrigger asChild>
                              <span
                                role="cell"
                                tabIndex={0}
                                className="flex min-w-0 flex-col text-[10px] text-(--cd-text-muted)"
                              >
                                {pairs.length === 0 ? (
                                  <span>—</span>
                                ) : (
                                  pairs.map((pair) => (
                                    <span
                                      key={topicPairLabel(pair)}
                                      className="truncate"
                                    >
                                      {topicPairLabel(pair)}
                                    </span>
                                  ))
                                )}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {pairs.length === 0
                                ? 'No topic tagged'
                                : pairs.map(topicPairLabel).join(' · ')}
                            </TooltipContent>
                          </Tooltip>
                        )
                      }
                      if (column.key === 'difficulty') {
                        return (
                          <span
                            role="cell"
                            key={column.key}
                            className="text-[10px] font-semibold"
                            style={{ color: DIFFICULTY_COLOR[item.difficulty] }}
                          >
                            {item.difficulty}
                          </span>
                        )
                      }
                      if (column.key === 'elo') {
                        if (item.elo === null) {
                          return (
                            <span
                              role="cell"
                              key={column.key}
                              aria-label="ELO unknown"
                              className="text-right font-mono text-[10px] font-bold text-(--cd-text-faint)"
                            >
                              —
                            </span>
                          )
                        }
                        if (band === null) {
                          return (
                            <span
                              role="cell"
                              key={column.key}
                              aria-label={`ELO ${item.elo}`}
                              className="text-right font-mono text-[10px] font-bold text-(--cd-text-muted)"
                            >
                              {item.elo}
                            </span>
                          )
                        }
                        return (
                          <Tooltip key={column.key}>
                            <TooltipTrigger asChild>
                              <span
                                role="cell"
                                tabIndex={0}
                                aria-label={`ELO ${item.elo}`}
                                className="text-right font-mono text-[10px] font-bold"
                                style={{ color: band.color }}
                              >
                                {item.elo}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {`ELO ${item.elo} · ${signed(band.delta)} vs ${elo === null ? '—' : `${elo.subject} (${elo.rating})`} · ${band.phrase} · optimal ${elo === null ? '—' : `${elo.lower}–${elo.upper}`}`}
                            </TooltipContent>
                          </Tooltip>
                        )
                      }
                      if (column.key === 'plans') {
                        return (
                          <Tooltip key={column.key}>
                            <TooltipTrigger asChild>
                              <span
                                role="cell"
                                tabIndex={0}
                                className="flex min-w-0 items-center gap-1 truncate text-[10px] text-(--cd-text-muted)"
                              >
                                <span className="truncate rounded-full bg-(--cd-inset) px-1.5 py-0.5">
                                  {item.plans[0] ?? '—'}
                                </span>
                                {item.plans.length > 1 ? (
                                  <span className="shrink-0 text-(--cd-text-faint)">
                                    +{item.plans.length - 1}
                                  </span>
                                ) : null}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {item.plans.join(' · ')}
                            </TooltipContent>
                          </Tooltip>
                        )
                      }
                      if (column.key === 'company') {
                        const companies = item.companies
                        return (
                          <Tooltip key={column.key}>
                            <TooltipTrigger asChild>
                              <span
                                role="cell"
                                tabIndex={0}
                                className={`flex min-w-0 items-center gap-1 truncate text-[10px] ${companies.length ? 'text-(--cd-text-muted)' : 'text-(--cd-text-faint)'}`}
                              >
                                <span className="truncate rounded-full bg-(--cd-inset) px-1.5 py-0.5">
                                  {companies[0] ?? 'General'}
                                </span>
                                {companies.length > 1 ? (
                                  <span className="shrink-0 text-(--cd-text-faint)">
                                    +{companies.length - 1}
                                  </span>
                                ) : null}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {companies.length
                                ? `Asked at: ${companies.join(' · ')}`
                                : 'General prep — no company tag'}
                            </TooltipContent>
                          </Tooltip>
                        )
                      }
                      if (column.key === 'fit') {
                        return (
                          <span
                            role="cell"
                            key={column.key}
                            className="text-[10px] font-bold text-(--cd-purple)"
                          >
                            {item.fit ?? '—'}
                          </span>
                        )
                      }
                      if (column.key === 'hours') {
                        return (
                          <span
                            role="cell"
                            key={column.key}
                            className="text-right text-[10px] text-(--cd-text-muted)"
                          >
                            {item.estimatedMinutes === null ? '—' : item.est}
                          </span>
                        )
                      }
                      return null
                    })}
                    <span role="cell" className="flex justify-end">
                      <AssignmentActions
                        variant="compact"
                        disabled={
                          curriculum.stagePending ||
                          item.alreadyAssigned === true
                        }
                        disabledHelp={
                          item.alreadyAssigned
                            ? 'Already in this plan — complete or cancel it to add again'
                            : undefined
                        }
                        // Adding stages whatever identity the row wears —
                        // through the validated resolver, so a stale pick
                        // stages the same fallback the row displays. An
                        // unpicked row sends null, keeping §2's default-add
                        // semantics (owning source at display time, no
                        // explicit sourceId written).
                        onAdd={() =>
                          curriculum.addAuto(
                            item,
                            pickedOption(item)?.sourceId ?? null,
                          )
                        }
                        onTray={() =>
                          curriculum.stageToTray(
                            item,
                            pickedOption(item)?.sourceId ?? null,
                          )
                        }
                        onTooltipOpenChange={(open) => {
                          if (open) {
                            suppressedRowTooltipId.current = item.id
                            setOpenRowTooltipId(null)
                          } else if (
                            suppressedRowTooltipId.current === item.id
                          ) {
                            suppressedRowTooltipId.current = null
                          }
                        }}
                      />
                    </span>
                  </div>
                )
                return (
                  <Tooltip
                    key={item.id}
                    open={openRowTooltipId === item.id}
                    onOpenChange={(open) =>
                      setOpenRowTooltipId((current) =>
                        open && suppressedRowTooltipId.current !== item.id
                          ? item.id
                          : current === item.id
                            ? null
                            : current,
                      )
                    }
                  >
                    <TooltipTrigger asChild>{row}</TooltipTrigger>
                    <TooltipContent>{rowTip}</TooltipContent>
                  </Tooltip>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {menuPosition ? (
        <ColumnMenu
          position={menuPosition}
          columns={menuColumns}
          hiddenCols={curriculum.hiddenCols}
          onToggle={(column) => {
            curriculum.toggleColumn(column)
            closeColumnMenu()
          }}
          onReorder={variant === 'browse' ? reorderColumn : undefined}
          onClose={closeColumnMenu}
        />
      ) : null}
    </div>
  )
}
